import React from 'react';
import { SESSION_TIME, webinarStart } from '../../data/webinars';
import type { Webinar } from '../../data/webinars';

/** The session date, written out in British English. */
export function sessionDateLabel(webinar: Webinar): string {
  return webinarStart(webinar).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
  });
}

/**
 * The start time in the visitor's own timezone, where the browser reports one.
 * Returns null when it matches GMT, so we never print the same time twice.
 */
function localTimeLabel(webinar: Webinar): {
  time: string;
  zone: string;
} | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!zone) return null;
    const start = webinarStart(webinar);
    const time = start.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: zone
    });
    const gmt = start.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC'
    });
    if (time === gmt) return null;
    return {
      time,
      zone: zone.split('/').pop()?.replace(/_/g, ' ') ?? zone
    };
  } catch {
    return null;
  }
}
interface SessionTimeProps {
  webinar: Webinar;
  reversed?: boolean;
  className?: string;
}

/**
 * Date, then the fixed GMT line with its regional conversions, then the
 * visitor's own time where we can work it out. The GMT line is always shown, so
 * the page is unambiguous even when the browser reports no timezone.
 */
export function SessionTime({
  webinar,
  reversed = false,
  className = ''
}: SessionTimeProps) {
  const [local, setLocal] = React.useState<{
    time: string;
    zone: string;
  } | null>(null);

  // Resolved after mount, since the timezone is a property of the visitor's
  // browser rather than of the page.
  React.useEffect(() => setLocal(localTimeLabel(webinar)), [webinar]);
  const strong = reversed ? 'text-white' : 'text-ink';
  const quiet = reversed ? 'text-white/72' : 'text-body';
  const faint = reversed ? 'text-white/55' : 'text-muted';
  return <div className={className} data-id="element-1431" data-mp="2lmzltkbr4ptv">
      <p className={`text-[16px] font-medium ${strong}`} data-id="element-1432" data-mp="2llwyfmdn15ip">
        {sessionDateLabel(webinar)}
      </p>
      <p className={`mt-1 text-[15px] ${quiet}`} data-id="element-1433" data-mp="27m5ez64cqwxb">{SESSION_TIME.full}</p>
      <p className={`mt-1 text-small ${faint}`} data-id="element-1434" data-mp="2cm1kxawm0yfi">
        {webinar.durationMins} minutes
        {local ? `, which is ${local.time} where you are (${local.zone})` : ''}
      </p>
    </div>;
}